import React, { useCallback, useMemo, useState } from 'react';
import './rule-editor-services.scss';
import { EditableRule, MultiSelectType } from 'src/model/EditableRule';
import { observer } from 'mobx-react-lite';
import { DefaultLabel, EntitySelector } from '../../../../shared/entity-selector/entity-selector';
import { useServicesCrudConfig } from '../../../inventory/services/services-crud-config';
import { createServiceColumns } from '../../../inventory/services/services-column-config';
import { useGenericHandler } from '../useGenericandler';
import { useStores } from '../../../../store';
import { Service } from '../../../../../dtos/version.dto';
import { DefaultServicesDto } from '../../../../../dtos/system-configuration.dto';
import { ServiceInfo } from './service-info';
import { FieldMessage } from '../../../../../dtos/error-message.dto';
import { InfoServiceMsg } from './info-msg-service';
import {
    isAllowedNonWebServiceRule,
    isAllowedServiceRule,
    isBlockServiceRule,
    isServiceRule,
    isWebServiceRule
} from 'src/utils/ruleUtils';
import { SystemConfiguration } from '../../../../../utils/system-configuration';
import { RuleHelper, RuleNameAlias } from '../../../../../utils/rule-helper';

interface RuleEditorServiceProps {
    ruleData: EditableRule;
    sectionErrors: { [field: string]: FieldMessage };
    handleRuleChange: (ruleData: Partial<EditableRule>) => void
}

const RuleEditorServices: React.FC<RuleEditorServiceProps> = observer(({
    ruleData,
    sectionErrors,
    handleRuleChange
}) => {
    const [hasChangeServices, setHasChangeServices] = useState<boolean>(false);
    const { ruleStore } = useStores();
    const serviceColumns = useMemo(() => createServiceColumns(ruleStore.specialServices), [ruleStore.specialServices]);

    const servicesRulesContainsAny = (ruleName: string) => {

        if (!isAllowedServiceRule(ruleName) && !isAllowedNonWebServiceRule(ruleName)) return false;

        const { MyNetworkToInternetServices,
        InternetToMyNetworkAllowedServices,
        AllowMyNetworkToInternetNonWebServices,
        AllowInternetToMyNetworkNonWebServices } = ruleStore;

        const negativeRules = {
            [RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_SERVICES)]: AllowMyNetworkToInternetNonWebServices,
            [RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_SERVICES)]: AllowInternetToMyNetworkNonWebServices,
            [RuleHelper.getRuleName(RuleNameAlias.ALLOW_TRUST_TO_UNTRUST_NON_WEB_SERVICES)]: MyNetworkToInternetServices,
            [RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_TO_TRUST_NON_WEB_SERVICES)]: InternetToMyNetworkAllowedServices
        };

        const negativeRule = negativeRules[ruleName];

        if (!negativeRule) return false;
        return negativeRule.service.services.length === serviceListByRuleName(negativeRule.displayName).length;
    };

    const alertDeactivate = useCallback(() => {
        const ruleName = ruleData.name;

        if (!isServiceRule(ruleName) || !hasChangeServices) return '';
        if (!ruleData.services.length) return 'If no services are selected, the rule will be deactivated.';
        if (!ruleData.active && ruleData.services.length) return 'If services are selected, the rule will be activated';

        const deactivateMsgBuilder = (deactivateRule: string) => `All services are selected, thus ${deactivateRule} will be deactivated.`;
        if (ruleData.services.length === serviceListByRuleName(ruleName).length) {
            if (isBlockServiceRule(ruleName)) return deactivateMsgBuilder(`${ruleData.srcZone} to ${ruleData.dstZone} Allowed Services rules`);
            if (servicesRulesContainsAny(ruleName)) return deactivateMsgBuilder(`${ruleData.srcZone} to ${ruleData.dstZone} Block Service rule`);
        }
        return '';
    }, [ruleData.services]);

    const convertToServiceType = (defaultService: DefaultServicesDto): Service => ({
        id: defaultService.serviceId,
        displayName: defaultService.serviceDisplayName,
        description: defaultService.serviceDescription,
        proto: defaultService.protocol,
        sports: [defaultService.sourcePort],
        dports: [defaultService.destinationPort],
        isDefault: true
    });

    const serviceListByRuleName = useCallback((ruleName: string) => {
        let defaultServices = SystemConfiguration.getInstance().defaultServiceDtoList.filter(_ => _.service);
        let customServices: Service[] = ruleStore.customServices;

        if(isBlockServiceRule(ruleName) || ruleData.action === 'drop' ) {
            customServices = ruleStore.customServicesWithoutSpecialNames;
        }else if (isAllowedServiceRule(ruleName)) {
            defaultServices = defaultServices.filter((_ => _.webService));
            customServices = [];
        } else if(isAllowedNonWebServiceRule(ruleName)) {
            defaultServices = defaultServices.filter((_ => !_.webService));
        }

        const mappedDefaultServices = defaultServices.map((_) => convertToServiceType(_));
        return [...mappedDefaultServices, ...customServices];
    }, [ruleStore.customServices]);

    const handleRuleChangeWithCheckServiceChange = (ruleDataEdited: Partial<EditableRule>) => {
        if (!hasChangeServices && JSON.stringify(ruleData.services) !== JSON.stringify(ruleDataEdited)) setHasChangeServices(true);
        handleRuleChange(ruleDataEdited);
    };

    const serviceHandler = useGenericHandler(
        serviceListByRuleName(ruleData.name),
        ruleData.services,
        'id',
        'services',
        handleRuleChangeWithCheckServiceChange
    );

    const resetServices = useCallback(() => {
        if (ruleData.servicesType !== MultiSelectType.ANY) {
            serviceHandler.removeAllItems();
            serviceHandler.handleTypeChange(MultiSelectType.ANY);
        }
    }, [serviceHandler, ruleData.servicesType]);

    const isDisabled = useCallback(() => {
        if (!ruleData.isDefault) {
            if (ruleData.applicationsType === MultiSelectType.SELECTED) {
                resetServices();
                return true;
            }
            return false;
        }

        return !isServiceRule(ruleData.name);
    }, [ruleData.isDefault, ruleData.applicationsType, ruleData.name]);

    return (
        <div className={'rule-editor-wrapper'}>
            <EntitySelector
                entityName={{ singular: 'Service', plural: 'Services' }}
                fullList={serviceListByRuleName(ruleData.name).map(_ => { return { name: _.displayName, key: _.id, data: _ }; }) || []}
                selectedItems={ruleData.services}
                type={ruleData.servicesType}
                EntityLabel={({ data }) => <DefaultLabel label={data.displayName} />}
                EntityInfo={({ data }) => <ServiceInfo service={data} />}
                handleSelection={serviceHandler.handleItemChange}
                handleBulkAllSelection={serviceHandler.handleAllItemsChange}
                handleTypeSelection={serviceHandler.handleTypeChange}
                handleRemove={serviceHandler.removeItem}
                error={sectionErrors['servicesType']}
                columnsConfig={serviceColumns}
                crudConfig={useServicesCrudConfig()}
                isTypeDisabled={isDisabled()}
                alertDeactivate={alertDeactivate()}
                disableSelectAny={isServiceRule(ruleData.name)}
                isNewEntityCreatable={!isWebServiceRule(ruleData.name)}
                InfoMsg={(data) => <InfoServiceMsg service={data} />}
            />
        </div>
    );
});

export default RuleEditorServices;